import { leadStageNames, type LeadCard, type LeadStage, type Me, type PipelineBoard, type PipelineFilter } from '@smarton-pbx/shared';
import { CalendarClock, Download, Phone, PhoneIncoming, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, photoUrl } from '../../api';
import { Avatar, Dialog, Loading, Notice, PageHeader } from '../../components/ui';
import { language, t, tp } from '../../i18n';
import { formatNumber } from '../../lib/calls';
import { useLoad } from '../../lib/hooks';
import { usePhone } from '../softphone';
import { LeadForm, LeadPanel } from './LeadPanel';
import { dueState, formatNextCall, leadTitle, localDay } from './leads';

const FILTER_KEY = 'pipeline-filter';

/** When leads were added: a few common spans, or two days to pick. */
type Added = 'any' | 'today' | 'week' | 'month' | 'days30' | 'dates';

/** The first day of each span (to today). */
const addedSince: Record<Exclude<Added, 'dates'>, () => string | undefined> = {
  any: () => undefined,
  today: () => localDay(),
  week: () => localDay(-6),
  month: () => `${localDay().slice(0, 8)}01`,
  days30: () => localDay(-29),
};

/** The person and source last picked (this browser only). */
function savedFilter(): PipelineFilter {
  try {
    const saved = JSON.parse(localStorage.getItem(FILTER_KEY) ?? '{}') as PipelineFilter;
    return { ...(saved.owner && { owner: saved.owner }), ...(saved.source && { source: saved.source }) };
  } catch {
    return {};
  }
}

/** The board with a card moved to another column at once (the server's order follows on reload). */
function withMoved(board: PipelineBoard, lead: LeadCard, stage: LeadStage): PipelineBoard {
  return {
    ...board,
    columns: board.columns.map((column) =>
      column.stage === lead.stage
        ? { ...column, count: column.count - 1, leads: column.leads.filter((l) => l.id !== lead.id) }
        : column.stage === stage
          ? { ...column, count: column.count + 1, leads: [{ ...lead, stage, talked: false }, ...column.leads] }
          : column,
    ),
  };
}

/**
 * The sales pipeline: a column for each stage a lead can be in, cards to
 * drag between them, and each lead's panel to call them from and note what
 * came of it. Everyone adds their own leads; managers export them to Excel.
 */
export function PipelinePage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const openId = params.get('lead');
  const [filter, setFilter] = useState<PipelineFilter>(savedFilter);
  const [q, setQ] = useState('');
  const [added, setAdded] = useState<Added>('any');
  const board = useLoad(() => api.pipeline(filter), [JSON.stringify(filter)]);
  const [more, setMore] = useState<Partial<Record<LeadStage, LeadCard[]>>>({});
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [dragging, setDragging] = useState<LeadCard | null>(null);
  const [dropOn, setDropOn] = useState<LeadStage | null>(null);
  const phone = usePhone();
  const manager = me.role !== 'AGENT';

  useEffect(() => {
    try {
      localStorage.setItem(FILTER_KEY, JSON.stringify({ owner: filter.owner, source: filter.source }));
    } catch {
      // Private window: the filter isn't remembered.
    }
  }, [filter.owner, filter.source]);

  // Search as they type, a moment after they stop.
  useEffect(() => {
    const timer = setTimeout(() => setFilter((f) => (f.q === (q.trim() || undefined) ? f : { ...f, q: q.trim() || undefined })), 250);
    return () => clearTimeout(timer);
  }, [q]);

  // Columns' further leads belong to the board they were loaded with.
  useEffect(() => setMore({}), [board.data]);

  // A call ended: its lead may have moved (nobody answered).
  useEffect(() => {
    if (phone.call?.state !== 'ended') return;
    const timer = setTimeout(board.reload, 1500);
    return () => clearTimeout(timer);
  }, [phone.call?.state, board.reload]);

  function pickAdded(choice: Added) {
    setAdded(choice);
    // Picking days: from the span shown so far, to change.
    if (choice !== 'dates') setFilter((f) => ({ ...f, addedFrom: addedSince[choice](), addedTo: undefined }));
  }

  const open = (id: string) =>
    setParams((p) => {
      p.set('lead', id);
      return p;
    });
  const close = () =>
    setParams((p) => {
      p.delete('lead');
      return p;
    });

  const leadsOf = (stage: LeadStage) => [...(board.data?.columns.find((c) => c.stage === stage)?.leads ?? []), ...(more[stage] ?? [])];

  /** The lead after this one in its column, to work down a list. */
  function nextAfter(lead: { id: string; stage: LeadStage }): string | null {
    const column = leadsOf(lead.stage);
    const at = column.findIndex((l) => l.id === lead.id);
    return at >= 0 ? (column[at + 1]?.id ?? null) : (column[0]?.id ?? null);
  }

  async function showMore(stage: LeadStage) {
    try {
      const next = await api.listLeads(stage, leadsOf(stage).length, filter);
      setMore((m) => ({ ...m, [stage]: [...(m[stage] ?? []), ...next] }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function move(lead: LeadCard, stage: LeadStage) {
    if (lead.stage === stage) return;
    setError(undefined);
    board.setData((b) => b && withMoved(b, lead, stage));
    try {
      await api.moveLead(lead.id, { stage });
      // Following up: when?
      if (stage === 'FOLLOW_UP' && !lead.followUpOn) open(lead.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      board.reload();
    }
  }

  function call(lead: LeadCard) {
    const number = lead.contacts.find((c) => c.e164)?.e164;
    if (!number) return;
    phone.dial(number);
    open(lead.id);
  }

  const data = board.data;
  const canCall = phone.status === 'registered' && !phone.busy;
  // The one picked stays a choice when no lead has it any more.
  const picked = filter.source !== 'none' ? filter.source : undefined;
  const sources = data && picked && !data.sources.includes(picked) ? [...data.sources, picked] : (data?.sources ?? []);
  return (
    <div className={`stack pipeline ${openId ? 'with-lead' : ''}`}>
      <PageHeader
        title={t('Pipeline')}
        description={t('Everyone your team calls to sell to, by where they stand. Calls nobody answers move them by themselves.')}
      >
        {manager && (
          // What the board shows (as filtered), all of it, as an Excel file.
          <a className="button secondary" href={api.exportLeadsUrl(filter, language)} download data-testid="export-leads">
            <Download size={16} aria-hidden /> {t('Export')}
          </a>
        )}
        <button type="button" onClick={() => setCreating(true)} data-testid="add-lead">
          <Plus size={16} aria-hidden /> {t('New lead')}
        </button>
      </PageHeader>

      <div className="card filters">
        <select
          value={filter.owner ?? ''}
          onChange={(e) => setFilter((f) => ({ ...f, owner: e.target.value || undefined }))}
          aria-label={t('Whose leads')}
          data-testid="lead-owner-filter"
        >
          <option value="">{manager ? t('Everyone’s') : t('Mine and nobody’s')}</option>
          <option value="me">{t('Mine')}</option>
          <option value="none">{t('Nobody’s')}</option>
          {manager &&
            data?.people
              .filter((p) => p.id !== me.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
        </select>
        {(sources.length > 0 || filter.source) && (
          <select
            value={filter.source ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value || undefined }))}
            aria-label={t('Where they came from')}
            data-testid="lead-source-filter"
          >
            <option value="">{t('All sources')}</option>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
            <option value="none">{t('No source')}</option>
          </select>
        )}
        <select value={added} onChange={(e) => pickAdded(e.target.value as Added)} aria-label={t('When they were added')} data-testid="lead-added-filter">
          <option value="any">{t('Added any time')}</option>
          <option value="today">{t('Added today')}</option>
          <option value="week">{t('Added in the last 7 days')}</option>
          <option value="month">{t('Added this month')}</option>
          <option value="days30">{t('Added in the last 30 days')}</option>
          <option value="dates">{t('Added between…')}</option>
        </select>
        {added === 'dates' && (
          <span className="row date-range">
            <input
              type="date"
              value={filter.addedFrom ?? ''}
              max={filter.addedTo}
              onChange={(e) => setFilter((f) => ({ ...f, addedFrom: e.target.value || undefined }))}
              aria-label={t('Added from')}
              data-testid="lead-added-from"
            />
            <span className="subtle">–</span>
            <input
              type="date"
              value={filter.addedTo ?? ''}
              min={filter.addedFrom}
              onChange={(e) => setFilter((f) => ({ ...f, addedTo: e.target.value || undefined }))}
              aria-label={t('Added to')}
              data-testid="lead-added-to"
            />
          </span>
        )}
        <div className="search-box grow">
          <Search size={16} aria-hidden className="subtle" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Company, name, number, email, source…')}
            aria-label={t('Search leads')}
            data-testid="lead-search"
          />
        </div>
        <button
          type="button"
          className={`secondary ${filter.due ? 'active' : ''}`}
          aria-pressed={Boolean(filter.due)}
          onClick={() => setFilter((f) => ({ ...f, due: f.due ? undefined : true }))}
          data-testid="due-filter"
        >
          <CalendarClock size={16} aria-hidden /> {data ? tp(data.due, '{n} to call today', '{n} to call today') : t('To call today')}
        </button>
      </div>

      {(error ?? board.error) && <Notice kind="error">{error ?? board.error}</Notice>}
      {!data ? (
        <Loading />
      ) : (
        <div className="pipeline-board" data-testid="pipeline-board">
          {data.columns.map((column) => {
            const leads = leadsOf(column.stage);
            return (
              <section
                key={column.stage}
                className={`pipeline-column stage-${column.stage} ${dropOn === column.stage ? 'drop' : ''}`}
                aria-label={t(leadStageNames[column.stage])}
                data-testid={`column-${column.stage}`}
                onDragOver={(e) => {
                  if (!dragging) return;
                  e.preventDefault();
                  setDropOn(column.stage);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropOn(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDropOn(null);
                  if (dragging) void move(dragging, column.stage);
                }}
              >
                <header className="pipeline-column-head">
                  <span>{t(leadStageNames[column.stage])}</span>
                  <span className="count" data-testid="column-count">
                    {column.count}
                  </span>
                </header>
                <div className="pipeline-cards">
                  {leads.map((lead) => (
                    <LeadTile
                      key={lead.id}
                      lead={lead}
                      canCall={canCall}
                      onOpen={() => open(lead.id)}
                      onCall={() => call(lead)}
                      onDragStart={() => setDragging(lead)}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropOn(null);
                      }}
                    />
                  ))}
                  {leads.length === 0 && <p className="pipeline-empty">{t('No leads')}</p>}
                  {leads.length < column.count && (
                    <button type="button" className="ghost small" onClick={() => void showMore(column.stage)}>
                      {t('Show more ({n})', { n: column.count - leads.length })}
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <LeadPanel
        me={me}
        leadId={openId}
        people={data?.people ?? []}
        sources={data?.sources ?? []}
        nextAfter={nextAfter}
        onOpen={open}
        onClose={close}
        onChanged={board.reload}
      />
      <Dialog open={creating} onClose={() => setCreating(false)} title={t('New lead')} wide>
        {creating && (
          <LeadForm
            me={me}
            lead={null}
            people={data?.people ?? []}
            sources={data?.sources ?? []}
            onCancel={() => setCreating(false)}
            onSaved={(saved) => {
              setCreating(false);
              board.reload();
              open(saved.id);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** A lead on the board: who, when to call next, what happened last, and a button to call its first person with a number. */
function LeadTile({
  lead,
  canCall,
  onOpen,
  onCall,
  onDragStart,
  onDragEnd,
}: {
  lead: LeadCard;
  canCall: boolean;
  onOpen: () => void;
  onCall: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const title = leadTitle(lead);
  const first = lead.contacts[0];
  const reach = lead.contacts.find((c) => c.e164);
  const missed = lead.lastCall?.direction === 'INBOUND' && lead.lastCall.outcome !== 'ANSWERED';
  const dead = lead.lastCall?.reason === 'number-not-in-use';
  return (
    <article
      className="pipeline-card"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', lead.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid="lead-card"
      data-lead={lead.id}
    >
      <div className="lead-head">
        <button
          type="button"
          className="lead-title"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {title}
        </button>
        {lead.ownerName && (
          <span title={lead.ownerName}>
            <Avatar name={lead.ownerName} photo={lead.ownerId && photoUrl(lead.ownerId, lead.ownerPhotoVersion)} />
          </span>
        )}
      </div>
      {lead.company && first?.name && (
        <div className="lead-person">
          {first.name}
          {lead.contacts.length > 1 && <span className="subtle"> +{lead.contacts.length - 1}</span>}
        </div>
      )}
      <div className="lead-badges">
        {lead.followUpOn && (
          <span className={`lead-due ${dueState(lead.followUpOn, lead.followUpTime)}`} data-testid="lead-due">
            <CalendarClock size={12} aria-hidden /> {formatNextCall(lead.followUpOn, lead.followUpTime)}
          </span>
        )}
        {lead.talked && (
          <span className="pill is-brand" data-testid="lead-talked">
            {t('Talked · set the result')}
          </span>
        )}
        {missed && (
          <span className="pill is-danger">
            <PhoneIncoming size={12} aria-hidden /> {t('Missed their call')}
          </span>
        )}
        {dead && <span className="pill is-danger">{t('Number not in use')}</span>}
        {!lead.talked && lead.unansweredCalls > 0 && (
          <span className="lead-tries" data-testid="lead-tries">
            {tp(lead.unansweredCalls, 'Tried {n}×', 'Tried {n}×')}
          </span>
        )}
        {lead.source && (
          <span className="lead-source" data-testid="lead-source">
            {lead.source}
          </span>
        )}
        {lead.topic && <span className="lead-topic">{lead.topic}</span>}
      </div>
      <div className="lead-foot">
        <span className="tabular">{reach?.e164 ? formatNumber(reach.e164) : first?.email}</span>
        {reach && (
          <button
            type="button"
            className="ghost icon"
            aria-label={t('Call {name}', { name: reach.name && reach.name !== title ? `${reach.name}, ${title}` : title })}
            title={t('Call')}
            disabled={!canCall}
            onClick={(e) => {
              e.stopPropagation();
              onCall();
            }}
            data-testid="call-lead"
          >
            <Phone size={15} aria-hidden />
          </button>
        )}
      </div>
    </article>
  );
}
